// utils/whatsapp.js
// Send an image to a customer's WhatsApp via the SOTA backend (Maytapi).
// Used by Marketing Collators / creative sharing.
import { API_BASE } from "./api";

function dealerLoginPhone() {
  const direct = String(localStorage.getItem("loginPhone") || "").replace(/\D/g, "");
  if (direct.length >= 10) return direct.slice(-10);
  try {
    const d = JSON.parse(localStorage.getItem("dealer") || "{}");
    const p = String(d.phone || d["Contact No"] || "").replace(/\D/g, "");
    if (p.length >= 10) return p.slice(-10);
  } catch {}
  return "";
}

// Sends a public media URL (Drive/http) to one customer — image, PDF, audio
// or video. Maytapi fetches the URL server-side, so no large upload is needed.
export async function sendWhatsAppImage({ phone, imageUrl, caption, filename }) {
  try {
    const to = String(phone || "").replace(/\D/g, "").slice(-10);
    if (!/^[6-9]\d{9}$/.test(to)) return { success: false, error: "invalid phone" };
    if (!/^https?:\/\//i.test(String(imageUrl || "")))
      return { success: false, error: "media url must be public http(s)" };

    const res = await fetch(`${API_BASE}/api/send-creative`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-auth-phone": dealerLoginPhone(),
      },
      body: JSON.stringify({
        phone: to,
        imageUrl,
        caption: caption || "",
        filename: filename || "creative",
      }),
    });
    const data = await res.json().catch(() => ({}));
    return {
      success: res.ok && data.success,
      error: res.ok && data.success ? "" : data.message || "send failed",
    };
  } catch (e) {
    return { success: false, error: e?.message || "network error" };
  }
}

/* ─── Bulk via Meta Cloud API (dealer's own WhatsApp Business number) ───
   Enabled only for dealers listed in the Meta phones sheet (backend decides).
   The file is uploaded to Meta ONCE, then sent in chunks of 20. */
const META_CHUNK = 20;

async function metaFetch(path, init = {}) {
  const res = await fetch(`${API_BASE}/api/wa-meta${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      "x-auth-phone": dealerLoginPhone(),
    },
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok && data.success !== false, data };
}

// /media and /send answer { jobId } right away (the Apps Script step is slower
// than CloudFront's 30s limit); poll the job until it finishes.
const JOB_POLL_MS = 2000;
const JOB_MAX_MS = 5 * 60 * 1000;

async function metaJob(path, body) {
  const { ok, data } = await metaFetch(path, { method: "POST", body: JSON.stringify(body) });
  if (!ok || !data.jobId) return { ok, data };
  const started = Date.now();
  while (Date.now() - started < JOB_MAX_MS) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, JOB_POLL_MS));
    let poll;
    try {
      // eslint-disable-next-line no-await-in-loop
      poll = await metaFetch(`/jobs/${encodeURIComponent(data.jobId)}`);
    } catch {
      continue; // transient network error — keep polling
    }
    if (!poll.ok) return { ok: false, data: poll.data };
    if (poll.data.status === "done") return { ok: true, data: poll.data.result || {} };
    if (poll.data.status === "error") return { ok: false, data: poll.data.result || {} };
  }
  return { ok: false, data: { message: "WhatsApp request timed out" } };
}

export async function getMetaBulkStatus() {
  try {
    const { ok, data } = await metaFetch("/status");
    return { enabled: ok && !!data.enabled, metaPhone: data.metaPhone || "" };
  } catch {
    return { enabled: false, metaPhone: "" };
  }
}

// `image` = personalized dataURL (tried first), `imageUrl` = public Drive URL
// (fallback — the big dataURL body can be blocked by the CloudFront WAF).
// onResult({ phone, ok, error }) is awaited for every phone, in order.
export async function sendBulkViaMeta({ phones, image, imageUrl, filename, caption, onResult }) {
  const list = [...new Set((phones || []).map((p) => String(p || "").replace(/\D/g, "").slice(-10)))]
    .filter((p) => p.length === 10);
  const results = [];
  const report = async (r) => {
    results.push(r);
    await onResult?.(r);
  };

  let media = null;
  let uploadError = "";
  const bodies = [image && { image }, imageUrl && { imageUrl }].filter(Boolean);
  for (const body of bodies) {
    try {
      // eslint-disable-next-line no-await-in-loop
      // sourceUrl: the creative's link, saved as "Image URL" in the Send Log
      // even when the personalized dataURL is the file actually uploaded.
      const { ok, data } = await metaJob("/media", { ...body, filename, sourceUrl: imageUrl || "" });
      if (ok && data.mediaId) {
        media = data;
        break;
      }
      uploadError = data.message || "upload failed";
    } catch (e) {
      uploadError = e?.message || "network error";
    }
  }
  if (!media) {
    for (const phone of list) {
      // eslint-disable-next-line no-await-in-loop
      await report({ phone, ok: false, error: uploadError || "no media" });
    }
    return results;
  }

  for (let i = 0; i < list.length; i += META_CHUNK) {
    const chunk = list.slice(i, i + META_CHUNK);
    let chunkResults;
    try {
      // eslint-disable-next-line no-await-in-loop
      const { ok, data } = await metaJob("/send", {
        phones: chunk,
        mediaId: media.mediaId,
        kind: media.kind,
        caption: caption || "",
        filename: media.filename,
        imageUrl: imageUrl || "", // saved as "Image URL" in the Send Log
      });
      chunkResults =
        ok && Array.isArray(data.results)
          ? data.results
          : chunk.map((phone) => ({ phone, ok: false, error: data.message || "send failed" }));
    } catch (e) {
      chunkResults = chunk.map((phone) => ({ phone, ok: false, error: e?.message || "network error" }));
    }
    for (const r of chunkResults) {
      // eslint-disable-next-line no-await-in-loop
      await report(r);
    }
  }
  return results;
}
